/**
 * Lista de puntos del recorrido.
 *
 * No es un extra ni una vista alternativa opcional: es la version accesible del
 * mapa. Contiene la misma informacion (nombre, estado, distancia) en un formato
 * que se puede leer, navegar por teclado y ampliar.
 *
 * Se muestra siempre, tambien con el mapa visible, porque en la calle y a
 * pleno sol una lista con distancias se consulta mejor que un mapa.
 *
 * El numero de cada punto es el mismo sello que se dibuja en el mapa, con los
 * mismos tres estados y los mismos colores. Que sean el mismo objeto en los dos
 * sitios es lo que permite mirar el mapa, ver un "7" y buscarlo en la lista sin
 * tener que traducir nada.
 */

import { useTranslation } from 'react-i18next';
import { formatearDistancia, type PuntoPublico } from '@recorregut/shared';
import { etiquetaFormato } from '@/i18n';

interface Props {
  puntos: PuntoPublico[];
  visitados: Set<number>;
  respondidos: Set<number>;
  dentroDeRadio: Set<number>;
  distancias: Map<number, number>;
  alAbrirPunto: (punto: PuntoPublico) => void;
}

export function ListaPuntos({
  puntos,
  visitados,
  respondidos,
  dentroDeRadio,
  distancias,
  alAbrirPunto,
}: Props) {
  const { t } = useTranslation();
  const formato = etiquetaFormato();

  if (puntos.length === 0) {
    return (
      <div className="px-4 py-10 sm:px-6">
        <p className="estado-vacio text-tinta-500">{t('punt.cap')}</p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-crema-200" aria-label={t('punt.llistaTitol')}>
      {puntos.map((punto, indice) => {
        const distancia = distancias.get(punto.id);
        const estaDentro = dentroDeRadio.has(punto.id);
        const respondido = respondidos.has(punto.id);
        const visitado = visitados.has(punto.id);

        const estado = respondido
          ? t('punt.respost')
          : visitado
            ? t('punt.visitat')
            : t('punt.noVisitat');

        return (
          <li key={punto.id}>
            <button
              type="button"
              onClick={() => {
                alAbrirPunto(punto);
              }}
              className="flex w-full items-center gap-4 px-4 py-4 text-left transition-colors
                         hover:bg-crema-100 sm:px-6"
            >
              <span
                className={`sello ${
                  respondido
                    ? 'sello-respondido'
                    : visitado
                      ? 'sello-visitado'
                      : 'sello-pendiente'
                }`}
                aria-hidden="true"
              >
                {indice + 1}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-tinta-900">{punto.nombre}</span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-tinta-500">
                  {/* El estado se escribe con palabra, no solo con color: el
                      color por si solo no es informacion accesible. */}
                  <span>{estado}</span>
                  {distancia !== undefined ? (
                    estaDentro ? (
                      /* Estar dentro del radio es LA senal que se busca en la
                         calle, asi que se destaca en lugar de quedar en la
                         misma linea gris que el resto. */
                      <span className="distintivo distintivo-activo">{t('punt.arribat')}</span>
                    ) : (
                      <span className="tabular-nums">
                        {t('punt.distancia', {
                          distancia: formatearDistancia(distancia, formato),
                        })}
                      </span>
                    )
                  ) : null}
                </span>
              </span>

              <svg
                aria-hidden="true"
                viewBox="0 0 20 20"
                fill="none"
                className="size-5 shrink-0 text-tinta-400"
              >
                <path
                  d="m7.5 4 6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
